/**
 * DownloadedAssetsSection - Polished grid display for downloaded media assets
 * 3-column grid with rounded cards, preview dialog, and nice overlay
 */

import { useState } from "react";

import { FolderOpen, Loader2, Trash2, Check, Copy, Link2, Music } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { LocalMediaAsset } from "@/types/media-library-panel.types";
import { formatFileSize } from "./media-utils";
import { useInfiniteScroll } from "./use-infinite-scroll";
import { cn } from "@/lib/utils";

interface DownloadedAssetsSectionProps {
  assets: LocalMediaAsset[];
  totalAssets: number;
  selectedIds: Set<string>;
  onToggleSelection: (id: string) => void;
  onSelectAll: () => void;
  onClearSelection: () => void;
  onDeleteSelected: () => void;
  isLoading?: boolean;
  isDeleting?: boolean;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
}

export function DownloadedAssetsSection({
  assets,
  totalAssets,
  selectedIds,
  onToggleSelection,
  onSelectAll,
  onClearSelection,
  onDeleteSelected,
  isLoading = false,
  isDeleting = false,
  hasMore = false,
  isLoadingMore = false,
  onLoadMore,
}: DownloadedAssetsSectionProps): React.ReactElement {
  const [previewAsset, setPreviewAsset] = useState<LocalMediaAsset | null>(null);
  const [isCopied, setIsCopied] = useState(false);

  const sentinelRef = useInfiniteScroll({
    onLoadMore: onLoadMore ?? (() => {}),
    hasMore,
    isLoading: isLoadingMore,
  });

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader2 className="w-5 h-5 text-muted-foreground animate-spin" />
        <span className="text-xs text-muted-foreground mt-2">
          Loading assets...
        </span>
      </div>
    );
  }

  if (assets.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="w-12 h-12 rounded-full bg-muted/60 flex items-center justify-center mb-3">
          <FolderOpen className="w-5 h-5 text-muted-foreground/50" />
        </div>
        <p className="text-sm font-medium text-muted-foreground">No downloaded assets</p>
        <p className="text-xs text-muted-foreground/60 mt-1">
          Search and download media to see them here
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Actions bar */}
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
          {totalAssets} asset{totalAssets !== 1 ? "s" : ""}
        </span>
        <div className="flex items-center gap-1.5">
          {selectedIds.size > 0 ? (
            <>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2.5 text-xs"
                onClick={onClearSelection}
              >
                Clear
              </Button>
              <Button
                variant="destructive"
                size="sm"
                className="h-7 px-2.5 text-xs rounded-full gap-1"
                onClick={onDeleteSelected}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <>
                    <Trash2 className="w-3 h-3" />
                    Delete ({selectedIds.size})
                  </>
                )}
              </Button>
            </>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2.5 text-xs"
              onClick={onSelectAll}
            >
              Select All
            </Button>
          )}
        </div>
      </div>

      {/* 3-column grid */}
      <div className="grid grid-cols-3 gap-2">
        {assets.map((asset) => {
          const isSelected = selectedIds.has(asset.id);
          return (
            <div
              key={asset.id}
              onClick={() => setPreviewAsset(asset)}
              className={cn(
                "relative overflow-hidden rounded-xl cursor-pointer group",
                "transition-all duration-200",
                isSelected
                  ? "ring-2 ring-primary ring-offset-2 ring-offset-background shadow-md shadow-primary/10"
                  : "hover:shadow-lg hover:shadow-black/10",
              )}
            >
              {/* Selection indicator */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleSelection(asset.id);
                }}
                className={cn(
                  "absolute top-2 left-2 w-5 h-5 rounded-full flex items-center justify-center z-10",
                  "transition-all duration-200",
                  isSelected
                    ? "bg-primary shadow-sm"
                    : "bg-black/30 backdrop-blur-sm opacity-0 group-hover:opacity-100",
                )}
              >
                {isSelected ? (
                  <Check className="w-3 h-3 text-primary-foreground" />
                ) : (
                  <div className="w-2 h-2 rounded-full border-[1.5px] border-white" />
                )}
              </button>

              {/* Thumbnail */}
              {asset.type === "image" && asset.previewUrl ? (
                <img
                  src={asset.previewUrl}
                  alt={asset.name}
                  className="w-full aspect-[4/3] object-cover transition-transform duration-500 ease-out group-hover:scale-[1.03]"
                  loading="lazy"
                />
              ) : asset.type === "video" && asset.previewUrl ? (
                <video
                  src={asset.previewUrl}
                  className="w-full aspect-[4/3] object-cover"
                  preload="metadata"
                />
              ) : (
                <div className="w-full aspect-[4/3] bg-muted flex items-center justify-center">
                  {asset.type === "audio" ? (
                    <Music className="w-6 h-6 text-muted-foreground/30" />
                  ) : (
                    <FolderOpen className="w-6 h-6 text-muted-foreground/30" />
                  )}
                </div>
              )}

              {/* Info overlay */}
              <div className="absolute bottom-0 inset-x-0 pt-6 pb-1.5 px-2 bg-gradient-to-t from-black/50 via-black/15 to-transparent">
                <div className="flex items-center justify-between gap-1 text-[10px] text-white/85 font-medium">
                  <span className="capitalize truncate">{asset.type}</span>
                  <span className="tabular-nums shrink-0">
                    {formatFileSize(asset.size)}
                  </span>
                </div>
              </div>
            </div>
          );
        })}

        {/* Skeleton cards while loading more */}
        {isLoadingMore &&
          Array.from({ length: 3 }).map((_, i) => (
            <div
              key={`skeleton-${i}`}
              className="rounded-xl overflow-hidden animate-pulse"
            >
              <div className="w-full aspect-[4/3] bg-muted" />
            </div>
          ))}
      </div>

      {/* Infinite scroll sentinel */}
      {hasMore && <div ref={sentinelRef} className="h-1" />}

      {/* Preview dialog */}
      <Dialog open={previewAsset !== null} onOpenChange={(open) => { if (!open) setPreviewAsset(null); }}>
        {previewAsset && (
          <DialogContent className="max-w-[92vw] sm:max-w-3xl max-h-[85dvh] p-0 overflow-hidden bg-black/95 border-border gap-0 rounded-lg">
            <DialogTitle className="sr-only">{previewAsset.name}</DialogTitle>
            <div className="relative flex items-center justify-center">
              {previewAsset.type === "image" && previewAsset.previewUrl ? (
                <img
                  src={previewAsset.previewUrl}
                  alt={previewAsset.name}
                  className="w-full object-contain max-h-[60dvh]"
                />
              ) : previewAsset.type === "video" && previewAsset.previewUrl ? (
                <video
                  src={previewAsset.previewUrl}
                  controls
                  autoPlay
                  className="w-full object-contain max-h-[60dvh]"
                  playsInline
                />
              ) : previewAsset.type === "audio" && previewAsset.previewUrl ? (
                <div className="w-full flex flex-col items-center justify-center py-16 gap-4">
                  <div className="w-16 h-16 rounded-full bg-muted/20 flex items-center justify-center">
                    <Music className="w-8 h-8 text-muted-foreground/50" />
                  </div>
                  <audio
                    src={previewAsset.previewUrl}
                    controls
                    autoPlay
                    className="w-4/5 max-w-md"
                  />
                </div>
              ) : (
                <div className="w-full flex items-center justify-center py-20">
                  <FolderOpen className="w-12 h-12 text-muted-foreground/30" />
                </div>
              )}
            </div>
            <div className="px-4 py-3 bg-background space-y-2 overflow-hidden">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{previewAsset.name}</p>
                  <p className="text-xs text-muted-foreground">
                    <span className="capitalize">{previewAsset.type}</span>
                    {" · "}
                    {formatFileSize(previewAsset.size)}
                  </p>
                </div>
              </div>
              {previewAsset.previewUrl && (
                <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-muted/40 border border-border/50 overflow-hidden">
                  <Link2 className="w-3 h-3 text-muted-foreground/50 flex-shrink-0" />
                  <span
                    className="flex-1 min-w-0 text-xs text-muted-foreground/70 truncate font-mono"
                    title={previewAsset.previewUrl}
                  >
                    {previewAsset.assetPath}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0 flex-shrink-0 hover:bg-muted-foreground/10"
                    onClick={() => {
                      navigator.clipboard.writeText(previewAsset.assetPath);
                      setIsCopied(true);
                      setTimeout(() => setIsCopied(false), 1200);
                    }}
                  >
                    {isCopied ? (
                      <Check className="w-3 h-3 text-primary" />
                    ) : (
                      <Copy className="w-3 h-3 text-muted-foreground/50" />
                    )}
                  </Button>
                </div>
              )}
            </div>
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
}
